import React from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredDropdownPosition } from '../../hooks/useAnchoredDropdownPosition';
import { useMaintenanceView } from './MaintenanceView';

export type MaintenanceActionTone = 'default' | 'danger';

export type MaintenanceAction = {
  key: string;
  label: React.ReactNode;
  href?: string | null;
  disabled?: boolean;
  tone?: MaintenanceActionTone;
  title?: string;
  onSelect?: () => void | Promise<void>;
};

export type MaintenanceActionsDropdownProps = {
  actions: MaintenanceAction[];
  label?: string;
  menuLabel?: string;
  stopPropagation?: boolean;
  /** Read-only details shown beneath the actions, e.g. who created the record. */
  footer?: React.ReactNode;
  /**
   * Only the ⋯ button and its menu, for rows that render their own primary action
   * (DirectoryRowActions). Renders nothing when there are no actions or footer.
   */
  menuOnly?: boolean;
};

export default function MaintenanceActionsDropdown({
  actions,
  label = 'Actions',
  menuLabel = 'Row actions',
  stopPropagation = true,
  footer,
  menuOnly = false,
}: MaintenanceActionsDropdownProps) {
  const availableActions = React.useMemo(() => actions.filter(Boolean), [actions]);
  const isDirectory = useMaintenanceView() === 'directory' && !menuOnly;
  const primaryAction = isDirectory
    ? availableActions.find((action) => action.tone !== 'danger' && /^(edit|manage|open|view|configure|contents|review|rename|notes)(-|$)/.test(action.key))
    : undefined;
  const menuActions = primaryAction ? availableActions.filter((action) => action.key !== primaryAction.key) : availableActions;
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const menuId = React.useId();
  const initialMenuFocus = React.useRef<'first' | 'last'>('first');
  const menuPosition = useAnchoredDropdownPosition(open, buttonRef, {
    width: 200,
    estimatedHeight: Math.max(96, availableActions.length * 42 + 16 + (footer ? 88 : 0)),
  });

  const menuItems = React.useCallback(() => Array.from(
    menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [],
  ), []);

  React.useLayoutEffect(() => {
    if (!open || !menuPosition || menuRef.current?.contains(document.activeElement)) return;
    const items = menuItems();
    ((initialMenuFocus.current === 'last' ? items[items.length - 1] : items[0]) ?? menuRef.current)?.focus();
  }, [open, menuPosition, menuItems]);

  React.useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(target) &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const toggleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (availableActions.length === 0 && !footer) return;
    initialMenuFocus.current = 'first';
    setOpen((current) => !current);
  };

  const close = () => setOpen(false);

  const singleAction = availableActions.length === 1 ? availableActions[0] : null;

  const handleActionClick = (
    action: MaintenanceAction,
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (action.disabled) {
      event.preventDefault();
      return;
    }
    close();
    if (menuRef.current?.contains(event.currentTarget)) buttonRef.current?.focus();
    if (action.onSelect) {
      void action.onSelect();
    }
  };

  if (menuOnly && availableActions.length === 0 && !footer) return null;

  if (singleAction && !isDirectory && !footer && !menuOnly) {
    const className = 'btn micro maintenance-action-explicit-button';
    const content = <span className="maintenance-action-explicit-button__label">{singleAction.label}</span>;

    if (singleAction.href && !singleAction.disabled) {
      return (
        <div className="maintenance-actions-dropdown" data-single-action="true">
          <a
            href={singleAction.href}
            className={className}
            data-tone={singleAction.tone || 'default'}
            title={singleAction.title}
            onClick={(event) => handleActionClick(singleAction, event)}
          >
            {content}
          </a>
        </div>
      );
    }

    return (
      <div className="maintenance-actions-dropdown" data-single-action="true">
        <button
          type="button"
          className={className}
          data-tone={singleAction.tone || 'default'}
          title={singleAction.title}
          disabled={singleAction.disabled}
          onClick={(event) => handleActionClick(singleAction, event)}
        >
          {content}
        </button>
      </div>
    );
  }

  const menu =
    open && menuPosition ? (
      <div
        ref={menuRef}
        id={menuId}
        className="dropdown-menu maintenance-action-menu"
        role="menu"
        tabIndex={-1}
        aria-label={typeof label === 'string' ? label : menuLabel}
        aria-describedby={footer ? `${menuId}-footer` : undefined}
        onKeyDown={(event) => {
          const items = menuItems();
          if (event.key === 'Tab') {
            setOpen(false);
            buttonRef.current?.focus();
            return;
          }
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || !items.length) return;
          event.preventDefault();
          event.stopPropagation();
          const index = items.indexOf(document.activeElement as HTMLElement);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
            : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
          items[next]?.focus();
        }}
        style={{
          position: 'fixed',
          top: menuPosition.top,
          left: menuPosition.left,
          zIndex: 10000,
          minWidth: 200,
        }}
        onClick={(event) => {
          if (stopPropagation) event.stopPropagation();
        }}
      >
        {menuActions.length ? <div className="dropdown-menu__section">
          {menuActions.map((action) => {
            const className = 'dropdown-menu__item maintenance-action-menu__item';
            const content = <span className="maintenance-action-menu__label">{action.label}</span>;
            if (action.href && !action.disabled) {
              return (
                <a
                  key={action.key}
                  href={action.href}
                  className={className}
                  role="menuitem"
                  tabIndex={-1}
                  data-tone={action.tone || 'default'}
                  title={action.title}
                  onClick={(event) => handleActionClick(action, event)}
                >
                  {content}
                </a>
              );
            }
            return (
              <button
                key={action.key}
                type="button"
                className={className}
                role="menuitem"
                tabIndex={-1}
                data-tone={action.tone || 'default'}
                title={action.title}
                disabled={action.disabled}
                onClick={(event) => handleActionClick(action, event)}
              >
                {content}
              </button>
            );
          })}
        </div> : null}
        {footer ? <div id={`${menuId}-footer`} className="dropdown-menu__section maintenance-action-menu__footer">{footer}</div> : null}
      </div>
    ) : null;

  return (
    <div className={`maintenance-actions-dropdown${isDirectory ? ' maintenance-directory-actions' : ''}`}>
      {primaryAction ? (
        primaryAction.href && !primaryAction.disabled ? (
          <a className="maintenance-directory-primary" href={primaryAction.href} title={primaryAction.title} onClick={(event) => handleActionClick(primaryAction, event)}>
            {primaryAction.label}<span aria-hidden="true">›</span>
          </a>
        ) : (
          <button type="button" className="maintenance-directory-primary" title={primaryAction.title} disabled={primaryAction.disabled} onClick={(event) => handleActionClick(primaryAction, event)}>
            {primaryAction.label}<span aria-hidden="true">›</span>
          </button>
        )
      ) : isDirectory ? <span /> : null}
      {menuActions.length > 0 || footer || !isDirectory ? <button
        ref={buttonRef}
        type="button"
        className="btn micro maintenance-action-button"
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        aria-label={menuLabel}
        aria-expanded={open}
        disabled={availableActions.length === 0 && !footer}
        onClick={toggleOpen}
        onKeyDown={(event) => {
          if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          if (stopPropagation) event.stopPropagation();
          initialMenuFocus.current = event.key === 'ArrowUp' ? 'last' : 'first';
          setOpen(true);
        }}
        style={{ minWidth: '32px', padding: '0.25rem 0.5rem' }}
      >
        {'\u22EF'}
      </button> : <span className="maintenance-directory-actions__placeholder" aria-hidden="true" />}
      {typeof document !== 'undefined' && menu ? createPortal(menu, document.body) : null}
    </div>
  );
}
